/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  LIST_GROUPS,
  useCurrentWorkspace,
  useGroupListFilter,
  useGroupListOrder,
  useSetGroupListOrder,
  OrderByInput,
} from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteDataGrid } from '../infiniteDataGrid';
import { GroupTableData, useColumns } from './useColumns';

export type GroupListProps = {
  maxHeight?: number;
  paddingBottom?: number;
  preserveScrollKey?: string;
  assetButton?: (data: GroupTableData) => React.ReactNode;
  peopleButton?: (data: GroupTableData) => React.ReactNode;
  routinesButton?: (data: GroupTableData) => React.ReactNode;
  editGroupMenu: (props: { groupId: string; label: string }) => React.ReactNode;
  createAlertButton: (groupId: string) => React.ReactNode;
};

export const GroupsList = ({
  maxHeight,
  paddingBottom,
  preserveScrollKey,
  assetButton,
  peopleButton,
  routinesButton,
  editGroupMenu,
  createAlertButton,
}: GroupListProps) => {
  const { t } = useTranslation();

  const AssetButton = assetButton;
  const PeopleButton = peopleButton;
  const RoutinesButton = routinesButton;
  const EditGroupMenu = editGroupMenu;

  const { data } = useGroupListFilter();
  const { data: orderData } = useGroupListOrder();
  const { mutateAsync: setOrder } = useSetGroupListOrder();
  const workspace = useCurrentWorkspace(true);

  const setOrderData = useCallback(
    (order: OrderByInput) => {
      setOrder(order);
    },
    [setOrder]
  );

  const [filter, setFilter] = useState<any>(undefined);
  const [groupsSelected, setGroupsSelected] = useState<string[] | undefined>(
    undefined
  );

  const showGroupMenu = useMemo(() => {
    return groupsSelected?.length === 1 ? true : false;
  }, [groupsSelected]);

  const updateGroupSelection = (rows: any) => {
    setGroupsSelected(rows);
  };

  useEffect(() => {
    if (!data || workspace === undefined) return;
    const f = { ...data };
    f.workspaceId = workspace?.id;
    if (f.keyword === '') delete f.keyword;
    setFilter(f);
  }, [data, workspace]);

  const columns = useColumns(filter, AssetButton, PeopleButton, RoutinesButton) as ColumnDef<
    any,
    any
  >[];

  const RenderHeader = () => {
    const selectedId = showGroupMenu && groupsSelected ? groupsSelected[0] : undefined;
    return selectedId ? (
      <>
        <EditGroupMenu groupId={selectedId} label={t('groups.menu.editGroup')} />
        {createAlertButton(selectedId)}
      </>
    ) : (
      <></>
    );
  };

  return !filter ? null : (
    <InfiniteDataGrid
      modelName="group"
      query={LIST_GROUPS}
      orderBy={orderData}
      filter={filter}
      pageSize={50}
      columns={columns}
      listMaxHeight={maxHeight as number}
      paddingBottom={paddingBottom as number}
      textEmpty={t('groups.common.textEmpty')}
      dataPrefix="group.listGroups"
      enableScrollRefresh={true}
      preserveScrollKey={preserveScrollKey}
      showScrollIndicator={true}
      exportFileName="groups"
      updateRowsSelected={(rows) => updateGroupSelection(rows)}
      headerContent={RenderHeader()}
      onOrderByChange={setOrderData}
    />
  );
};
